/**
 * The account-creation form.
 *
 * ONE component because it is rendered in two places: the standalone /signup
 * page and the AccountPrompt modal a tournament raises when somebody taps
 * Register while signed out. The two differ only in what context they can hand
 * it, never in the form itself.
 *
 * Three fields, matching POST /api/auth/register exactly — email, password,
 * display name. No phone, and NO NICKNAME: a nickname is something a player
 * picks once they have seen themselves on a board, and PUT /api/auth/me is
 * where they set it. register.js says the same at its head.
 *
 * MIN_PASSWORD is MIRRORED from functions/lib/accounts.js rather than
 * imported — nothing in src/ reaches into functions/. Checking it here saves a
 * round trip; the backend remains the authority, and its refusals are rendered
 * verbatim rather than reworded, including the deliberate "that email already
 * has an account".
 *
 * ON SUCCESS THIS COMPLETES NOTHING ELSE. A player who was part-way through
 * registering for a tournament is NOT entered into it — see Profile.jsx.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { profilePathFor } from '../../lib/signupReturn.js';

/** Mirrors MIN_PASSWORD in functions/lib/accounts.js. */
const MIN_PASSWORD = 8;

export default function SignUpForm({ context = null, onSuccess = null }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  async function onSubmit(event) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }

    setBusy(true);
    const result = await register(email, password, name);

    if (!result.ok) {
      setError(result.error);
      setBusy(false);
      return;
    }

    // The modal wants to close itself and route as one act; the page just
    // routes. Either way the destination is the same profile.
    if (onSuccess) onSuccess(result.user);
    else navigate(profilePathFor(context), { replace: true });
  }

  return (
    <form className="hd-stack" onSubmit={onSubmit}>
      <div className="hd-field">
        <label className="hd-label" htmlFor="signup-name">Display name</label>
        <input
          id="signup-name"
          className="hd-input"
          type="text"
          autoComplete="name"
          required
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
        <span className="hd-hint">This is the name that appears on the board.</span>
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="signup-email">Email</label>
        <input
          id="signup-email"
          className="hd-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          disabled={busy}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="signup-password">Password</label>
        <input
          id="signup-password"
          className="hd-input"
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD}
          value={password}
          disabled={busy}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span className="hd-hint">At least {MIN_PASSWORD} characters.</span>
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
        {busy ? 'Creating your account…' : 'Create account'}
      </button>
    </form>
  );
}
